import Page from "@/components/layout/Page";
import * as Styled from "./styled";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import useApi from "@/contexts/ApiContext";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import DocLayout from "@/components/layout/DocLayout";
import { AnchorItem } from "../Documentation/styled";
import useDocsActiveHeading from "@/contexts/DocsActiveHeadingContext";
import DatoBlockRenderer from "@/components/shared-ui/DatoBlockRenderer";
import Card from "@/components/shared-ui/Card";
import {
  communicationTimelinessLookup,
  currentEngagementLookup,
  getTransferProviderStatus,
  relationshipStrengthLookup,
} from "../TransferProviderList/statusLookup";
import {
  StatusDot,
  StatusPill,
} from "../TransferProviderList/TransferProviderList";
import FrozenHeaderTable from "@/components/shared-ui/DatoBlockRenderer/FrozenHeaderTable";
import sanitizeHtml from "sanitize-html";
import { sanitizeHtmlOptions } from "@/utils/sanitize.utils";
import { Fragment } from "react";
import AnchorHeading from "../Documentation/AnchorHeading";
import axios from "axios";
import NotFound from "../NotFound";
import type { StructuredTextContentWithoutBlocks } from "@/services/api/types";

const hasStructuredTextValue = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasStructuredTextValue);
  if (!value || typeof value !== "object") return false;

  return Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !["type", "schema", "marks"].includes(key))
    .some(([, child]) => hasStructuredTextValue(child));
};

const hasStructuredTextContent = (
  content: StructuredTextContentWithoutBlocks | null,
): content is StructuredTextContentWithoutBlocks =>
  Boolean(content && hasStructuredTextValue(content.value));

const hasHtmlContent = (html: string | null | undefined): html is string =>
  Boolean(
    html &&
      sanitizeHtml(html, { allowedTags: [], allowedAttributes: {} }).trim(),
  );

const TransferProvider = () => {
  const api = useApi();
  const { transferProviderSlug } = useParams();
  const { activeId } = useDocsActiveHeading();

  const { data: transferProvider, error } = useQuery({
    queryKey: ["get-transfer-provider", transferProviderSlug],
    queryFn: () => api.transferProviders.get(transferProviderSlug || ""),
    placeholderData: keepPreviousData,
    retry: (failureCount, queryError) =>
      !(
        axios.isAxiosError(queryError) && queryError.response?.status === 404
      ) && failureCount < 3,
    throwOnError: (queryError) =>
      !(
        axios.isAxiosError(queryError) && queryError.response?.status === 404
      ),
  });

  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return <NotFound />;
  }

  if (!transferProvider) {
    return <LoadingScreen />;
  }

  const relationshipStrength =
    getTransferProviderStatus(
      relationshipStrengthLookup,
      transferProvider.relationshipStrength,
    );
  const currentEngagement =
    getTransferProviderStatus(
      currentEngagementLookup,
      transferProvider.currentEngagement,
    );
  const communicationTimeliness =
    getTransferProviderStatus(
      communicationTimelinessLookup,
      transferProvider.communicationTimeliness,
    );
  const commonRejections = transferProvider.commonRejections;

  const relationshipFields = [
    { label: "Relationship strength", status: relationshipStrength },
    { label: "Current engagement", status: currentEngagement },
    {
      label: "Communication timeliness",
      status: communicationTimeliness,
    },
  ];

  const renderTable = (html: string) => (
    <FrozenHeaderTable
      html={sanitizeHtml(html, sanitizeHtmlOptions)}
      variant="rowDividers"
    />
  );

  const sections = [
    {
      label: "Introduction",
      anchorLink: "summary",
      showHeading: false,
      content: <DatoBlockRenderer content={transferProvider.summary} />,
    },
    {
      label: "Relationship overview",
      anchorLink: "relationshipOverview",
      content: (
        <Styled.RelationshipWrapper>
          {relationshipFields.map(({ label, status }) => (
            <Card
              key={label}
              featureBackground={{ full: true, gradient: "light2" }}
            >
              <Styled.RelationshipBox>
                <StatusDot omitLabel {...status} />
                <div>
                  <div>{label}:</div>
                  <strong>{status.label}</strong>
                </div>
              </Styled.RelationshipBox>
            </Card>
          ))}
        </Styled.RelationshipWrapper>
      ),
    },
    {
      label: "Transfer requirements",
      anchorLink: "transferRequirements",
      content: hasHtmlContent(transferProvider.transferRequirements)
        ? renderTable(transferProvider.transferRequirements)
        : null,
    },
    {
      label: "Transfer processes and timescales",
      anchorLink: "transferProcessesAndTimescales",
      content: hasHtmlContent(transferProvider.transferProcessesAndTimescales)
        ? renderTable(transferProvider.transferProcessesAndTimescales)
        : null,
    },
    {
      label: "Processing capabilities",
      anchorLink: "processingCapabilities",
      content: hasHtmlContent(transferProvider.processingCapabilities)
        ? renderTable(transferProvider.processingCapabilities)
        : null,
    },
    {
      label: "Communication and chasing approach",
      anchorLink: "communicationAndChasingApproach",
      content: hasHtmlContent(
        transferProvider.communicationAndChasingApproach,
      )
        ? renderTable(transferProvider.communicationAndChasingApproach)
        : null,
    },
    {
      label: "SIPP requirements",
      anchorLink: "sippRequirements",
      content: hasHtmlContent(transferProvider.sippRequirements)
        ? renderTable(transferProvider.sippRequirements)
        : null,
    },
    {
      label: "Common rejections",
      anchorLink: "commonRejections",
      content: hasStructuredTextContent(commonRejections) ? (
        <DatoBlockRenderer content={commonRejections} />
      ) : null,
    },
  ].filter(({ content }) => content !== null);

  const sidebar = (
    <>
      <Styled.AnchorHeading>On this page</Styled.AnchorHeading>
      <div>
        {sections.map((section) => {
          return (
            <AnchorItem
              key={section.anchorLink}
              href={`#${section.anchorLink}`}
              $active={activeId === section.anchorLink}
            >
              {section.label}
            </AnchorItem>
          );
        })}
      </div>
      <Styled.AnchorHeading>Framework</Styled.AnchorHeading>
      <Styled.FrameworkFields>
        {relationshipFields.map(({ label, status }) => (
          <Styled.FrameworkField key={label}>
            <strong>{label}</strong>
            <StatusPill {...status} />
            <span>{status.description}</span>
          </Styled.FrameworkField>
        ))}
      </Styled.FrameworkFields>
    </>
  );

  const docContent = (
    <>
      {sections.map(({ label, anchorLink, content, showHeading = true }) => (
        <Fragment key={anchorLink}>
          <AnchorHeading
            heading={label}
            anchorSuitableId={anchorLink}
            visible={showHeading}
          />
          {content}
        </Fragment>
      ))}
    </>
  );

  return (
    <Page
      title={transferProvider.name}
      customBreadcrumb={{
        label: "Back to providers",
        path: "/transfer-providers",
      }}
      noPadding
      fullWidth
      stickyBreadcrumbs
      whiteBackground
    >
      <DocLayout
        primarySidebar={{
          visible: false,
          heading: "",
          menu: <></>,
          accordionHeading: "",
        }}
        secondarySidebar={{
          visible: true,
          menu: sidebar,
        }}
        docHeading={transferProvider.name}
        typographyStyle="update"
        docContent={docContent}
      />
    </Page>
  );
};

export default TransferProvider;
